import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { Store } from '@ngrx/store';
import { observable, Observable, of, Subject } from 'rxjs';
import {
  DEFAULT_ORDERS_LIST_LIMIT,
  DEFAULT_ORDERS_LIST_OFFSET
} from '../../constants';
import { OrderHistoryItem } from '../../models';
import {
  clearOrdersList,
  loadOrdersList,
  OrdersHistoryState,
  selectOrdersList,
  selectOrdersLoading,
  selectOrdersTotalCount,
} from '../../store/orders-history-list';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FeatureToggleEnum } from '~shared/constants';
import { catchError, filter, map, shareReplay, skip, take, takeUntil } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';
import { AccountInfoService } from '~app/core/modules/identity/services/account-info.service';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { OrderHistoryGaTrackingService } from '../../services/order-history-ga-tracking.service';
import { GaTrackingEventName } from '~shared/modules/ga-tracking/constants';
import { OrderHistoryEventActionType } from '~shared/modules/application-event/constants';
import { AccountInfoContext } from '~shared/constants/account-info.constant';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { FF_PAYMENT_METHOD_CODE } from '../../constants/orders-history-feature-flags';

@Component({
  selector: 'app-orders-history-list',
  templateUrl: './orders-history-list.component.html',
  styleUrls: ['./orders-history-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersHistoryListComponent implements OnInit, OnDestroy {
  public isMobileOrTablet = false;
  public isSubsidiary = false;
  public orders$: Observable<OrderHistoryItem[]> = this.store$.select(selectOrdersList);
  public totalCount: number;
  public loading$: Observable<boolean> = this.store$.select(selectOrdersLoading);
  public isContactEmailFeatureEnabled$: Observable<boolean> = this.featureFlagSrv
    .isFeatureEnabled(FeatureToggleEnum.FF_CONTACT_SELLER_EMAIL);
  public isGetInvoiceFeatureEnabled$: Observable<boolean> = this.featureFlagSrv
    .isFeatureEnabled(FeatureToggleEnum.FF_TEMP_GET_INVOICE_ORDER_LEVEL_BUYER_ORDERS_HISTORY);
  private unsubscribe$ = new Subject<void>();
  public isEnabledPaymentMethodCode$: Observable<boolean>;
  private readonly limit = DEFAULT_ORDERS_LIST_LIMIT;
  private offset = DEFAULT_ORDERS_LIST_OFFSET;
  public get isAllowedLoadMoreBtn() {
    return this.totalCount > (this.offset + this.limit);
  }
  isBuyAgainEnable: boolean = true;

  constructor(private store$: Store<OrdersHistoryState>,
    private featureFlagSrv: ActiveFeaturesConfigcatService,
    private mediaObserver: MediaObserver,
    private cd: ChangeDetectorRef,
    private accountInfoService: AccountInfoService,
    private orderHistoryGaTrackingService: OrderHistoryGaTrackingService,
    private activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.checkPersonalContext();
      this.checkBuyAgainButtonEnable();
      this.checkPaymentMethodCodeFFEnabled();
    }

    this.getTotalCount();
    this.initMediaObserver();
    this.subscribeForContextChange();
  }

  getTotalCount() {
    this.store$.select(selectOrdersTotalCount)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((totalCount: number) => this.totalCount = totalCount);
  }

  public loadMore() {
    this.sendLoadMoreButtonGAEvents();
    this.offset += this.limit;
    this.loadOrdersList();
  }

  private loadOrdersList() {
    this.store$.dispatch(loadOrdersList({ params: { offset: this.offset, limit: this.limit } }));
  }

  private initMediaObserver(): void {
    this.mediaObserver.asObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isMobileOrTablet = this.mediaObserver.isActive(MEDIA_ALISES.mobile)
          || this.mediaObserver.isActive(MEDIA_ALISES.tablet);

        this.cd.markForCheck();
      });
  }

  private subscribeForContextChange(): void {
    this.accountInfoService.getActiveContext().pipe(
      skip(1),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => {
      this.offset = DEFAULT_ORDERS_LIST_OFFSET;
      this.store$.dispatch(clearOrdersList());
      this.loadOrdersList();
    });
  }

  private sendLoadMoreButtonGAEvents(): void {
    this.orderHistoryGaTrackingService.gaTrackingOrderHisotryInteractionsEvent
      (GaTrackingEventName.OrderHistoryInteraction, OrderHistoryEventActionType.ShowNextOrdersButton, '');
  }

  private checkPersonalContext(): void {
    if (this.activatedRoute.snapshot.queryParams.context === AccountInfoContext.personal) {
      this.accountInfoService.setPersonalAccountInfo(AccountInfoContext.personal);
    }
    this.loadOrdersList();
    this.cd.markForCheck();
  }

  private checkBuyAgainButtonEnable(): void {
    this.accountInfoService.getAccountInfo()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(accountInformation => accountInformation !== null)
        ).subscribe((accountInformation: any) => {
        this.isSubsidiary = accountInformation?.isSubsidiary;
        if (
          accountInformation?.isSubsidiary ||
          (this.activatedRoute.snapshot.queryParams.context === AccountInfoContext.personal &&
            accountInformation.activeContext !== AccountInfoContext.personal)
        ) {
          this.isBuyAgainEnable = false;
        }
      });
  }

  private checkPaymentMethodCodeFFEnabled(): void {
    this.isEnabledPaymentMethodCode$ = this.featureFlagSrv.isFeatureEnabledAndReadyV2(FF_PAYMENT_METHOD_CODE).pipe(
      take(1),
      map((isEnabled) => isEnabled),
      shareReplay(1),
      catchError(() => {
        return of(false);
      })
    );
  }

  ngOnDestroy() {
    this.store$.dispatch(clearOrdersList());
    this.accountInfoService.setPersonalAccountInfo(null);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
